import { ActionType, createAction } from '@fullstory/redux-util/utils/types';
import { SearchActionTypes, SearchUIMode } from './types';
import {
  OmnisearchSuggestion,
  SearchFilterTypes,
  SearchGroup,
  SearchModel,
} from '@fullstory/search-model';
import { DependentType, SearchType, UserType } from '@fullstory/search-types';
import { UID, WithID } from '@fullstory/util/uid';
import { PlatformEnum, TimeRange } from '@fullstory/client-api';
import {
  CompletionGroup,
  ComputeScrollMapsStatsRequest,
  FieldSuggestRequest,
  SearchRequest,
  SearchServicePublic as svc,
  SearchServicePublic,
} from '@fullstory/proto/fs/services/alexa';
import { xhrPost } from '@fullstory/util/xhr';
import { AppThunkAction } from '../../types';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { int64 } from '@fullstory/proto/google/protobuf';
import jstz from 'jstz';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { EventSearch } from '@fullstory/proto/fs/api/search';

export const SearchActions = {
  setTimeRange: (timeRange: TimeRange) =>
    createAction({
      type: SearchActionTypes.SetTimeRange,
      data: timeRange,
    }),
  /**
   *  addRow will add a complete row to the search UI. This means that if the UI is in the default state,
   *  (i.e. no search criteria), then the default search group will be replaced with the provided search group.
   * @param group - the set of filters to add to (i.e. users, events, not events)
   * @param data - the search group to add
   */
  addRow: (group: SearchFilterTypes, data: SearchGroup<SearchType>, mode: SearchUIMode) =>
    createAction({
      type: SearchActionTypes.AddRow,
      group,
      data,
      mode,
    }),
  replaceOrAddUserRow: (data: SearchGroup<UserType>, mode: SearchUIMode) =>
    createAction({
      type: SearchActionTypes.ReplaceOrAddUserRow,
      data,
      mode,
    }),
  appendDependents: (
    group: SearchFilterTypes,
    dependents: WithID<DependentType>[],
    mode: SearchUIMode,
  ) =>
    createAction({
      type: SearchActionTypes.AppendDependents,
      group,
      data: dependents,
      mode,
    }),
  setInSameSession: (inSameSession: boolean) =>
    createAction({
      type: SearchActionTypes.SetInSameSession,
      inSameSession,
    }),
  setPlatform: (platform: PlatformEnum) =>
    createAction({
      type: SearchActionTypes.SetPlatform,
      platform,
    }),
  setModel: (model: SearchModel, syncSearchModel: boolean = false) =>
    createAction({
      type: SearchActionTypes.SetModel,
      model,
      syncSearchModel,
    }),
  setPendingSuggestion: (suggestion: OmnisearchSuggestion, mode: SearchUIMode) =>
    createAction({
      type: SearchActionTypes.SetPendingSuggestion,
      suggestion,
      mode,
    }),
  clearPendingSuggestion: () =>
    createAction({
      type: SearchActionTypes.ClearPendingSuggestion,
    }),
};

export type SearchActions = ActionType<typeof SearchActions>;

export type AutocompleteFilter = Partial<
  Pick<FieldSuggestRequest, 'dependentEventFilter' | 'eventFilter' | 'ignoreBefore' | 'userFilter'>
>;

export const SearchAsyncActions = {
  autocomplete:
    (filter: AutocompleteFilter, term: string): SearchThunkAction<Promise<CompletionGroup[]>> =>
    (_, __, { xhr, orgId }) =>
      SearchServicePublic.FieldSuggest(
        xhrPost(xhr(), {
          batchOptions: { id: SearchServicePublic.FieldSuggestEndpoint, maxOutgoingRequests: 0 },
        }),
        {
          orgId: orgId(),
          term,
          ignoreBefore: null,
          ...filter,
        },
      )
        .promise()
        .then(rsp => rsp.completionGroups),
};

export const SearchGrpcRequestActions = {
  scrollMapsFacet: (
    pageUrl: string,
    pageDevice: string,
    statsType: ComputeScrollMapsStatsRequest.StatsType,
    segmentDefinition: SegmentDefinition,
    limit: number,
    orgId: string,
    clusterId?: number,
    force?: boolean,
    pageViewUrl?: string,
    pageViewStart?: int64,
    pageViewName?: string,
    eventSearch?: EventSearch,
    pageDefId?: int64,
    specId?: string,
    uid?: UID,
  ) =>
    createGRPCAction<typeof svc.ComputeScrollMapsStats>({ uid })(
      {
        type: `${SearchServicePublic.ComputeScrollMapsStatsEndpoint}Request`,
        grpcFunction: svc.ComputeScrollMapsStats,
        body: {
          orgId,
          searchRequest: createSearchRequest(segmentDefinition, limit, eventSearch),
          pageUrl,
          pageDevice,
          clusterId: String(clusterId ?? '') as int64,
          statsType,
          cacheOptions: null,
          pageViewUrl: pageViewUrl ?? '',
          pageViewStart: pageViewStart ?? int64.fromNumber(0),
          pageViewName: pageViewName ?? '',
          pageDefId: int64.toNumber(pageDefId) ?? 0,
          specId: specId ?? '',
        },
        settings: {
          headers: { 'GRPC-Timeout': '1M' },
        },
        readWrite: 'write',
        force,
      },
      `${SearchServicePublic.ComputeScrollMapsStatsEndpoint}Success`,
      `${SearchServicePublic.ComputeScrollMapsStatsEndpoint}Failure`,
    ),
};

const createSearchRequest = (
  segmentDefinition: SegmentDefinition,
  limit: number,
  eventSearch?: EventSearch,
): SearchRequest => ({
  segmentDefinition,
  eventSearch: eventSearch ?? null,
  indvId: '0' as int64,
  userId: '0' as int64,
  sessionId: '0' as int64,
  start: 0,
  limit,
  now: null,
  timezone: jstz.determine().name(),
});

export type SearchThunkAction<R> = AppThunkAction<R, SearchActions>;
export type SearchGrpcActions = RequestActionType<typeof SearchGrpcRequestActions>;
